import { API_BASE_URL } from '../lib/constants';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  MenuItem,
  Alert,
  FormHelperText,
  Box
} from '@mui/material';
import theme from '../theme/theme';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import DraftSaveButton from '../components/DraftSaveButton';
import { DRAFT_KEYS } from '../utils/draftUtils';
import { ThemeProvider } from '@mui/material/styles';
// const role = [
//   'Admin',
//   'OT Manager',
//   'Doctor',
//   'Nurse',
//   'Staff',
// ];

// Initial fallback roles
const INITIAL_ROLES = [
  'Admin',
  'Doctor',
  'Nurse',
];



const EditUser = () => {
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState({
    username: '',
    fullname: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: '',
  });

  const [roles, setRoles] = useState(INITIAL_ROLES);
  const [errors, setErrors] = useState({
    username: '',
    fullname: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: '',
  });

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user_id } = useParams();

  const isEditMode = !!user_id;

  const handleNameChange = (e) => {
    const regex = /^[a-zA-Z\s.-]*$/;
    const value = e.target.value;
    if (value === '' || regex.test(value)) {
      setFormData({ ...formData, [e.target.name]: value });
    }
  };

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/get_roles`, { withCredentials: true });
        if (response.data.status === 'success' && Array.isArray(response.data.data)) {
          setRoles(response.data.data.map(r => typeof r === 'string' ? r : r.name));
        }
      } catch (err) {
        console.error('Error fetching roles:', err);
      }
    };
    fetchRoles();

    if (isEditMode) {
      axios.get(`${API_BASE_URL}/get_user/${user_id}`)
        .then((res) => {
          const user = res.data.data;
          setFormData({
            username: user.username,
            fullname: user.fullname,
            email: user.email,
            password: '',
            confirmPassword: '',
            role: user.role,
          });
        })
        .catch((err) => {
          console.error(err);
          showNotification('error', 'Failed to fetch user data. Please check your connection.');
        });
    }
  }, [user_id, isEditMode]);

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  const validatePassword = (password) => {
    if (password.length < 8) return "Password must be at least 8 characters";
    if (!/[A-Z]/.test(password)) return "Password must contain at least one uppercase letter";
    if (!/[a-z]/.test(password)) return "Password must contain at least one lowercase letter";
    if (!/[0-9]/.test(password)) return "Password must contain at least one number";
    return "";
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));

    if (name === 'password' && value) {
      const passwordError = validatePassword(value);
      setErrors(prev => ({ ...prev, password: passwordError }));
    }

    if (name === 'confirmPassword') {
      setErrors(prev => ({
        ...prev,
        confirmPassword: value !== formData.password ? 'Passwords do not match' : '',
      }));
    }

    if (name === 'email') {
      setErrors(prev => ({
        ...prev,
        email: value && !validateEmail(value) ? 'Invalid email format' : '',
      }));
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    let error = "";

    switch (name) {
      case 'username': if (!value) error = 'Username is required'; break;
      case 'fullname': if (!value) error = 'Full name is required'; break;
      case 'email':
        if (!value) error = 'Email is required';
        else if (!validateEmail(value)) error = 'Invalid email format';
        break;
      case 'role': if (!value) error = 'Role is required'; break;
      case 'password': if (value) error = validatePassword(value); break;
      case 'confirmPassword':
        if (formData.password && !value) error = 'Please confirm password';
        else if (value !== formData.password) error = 'Passwords do not match';
        break;
      default: break;
    }

    if (error) setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateForm = () => {
    const newErrors = {
      username: !formData.username ? 'Username is required' : '',
      fullname: !formData.fullname ? 'Full name is required' : '',
      email: !formData.email ? 'Email is required' : !validateEmail(formData.email) ? 'Invalid email format' : '',
      password: formData.password ? validatePassword(formData.password) : '',
      confirmPassword: (formData.password && !formData.confirmPassword) ? 'Please confirm password' : (formData.password !== formData.confirmPassword) ? 'Passwords do not match' : '',
      role: !formData.role ? 'Role is required' : '',
    };

    setErrors(newErrors);
    const hasError = Object.values(newErrors).some(err => err);
    if (hasError) {
      showNotification('error', 'Please correct the highlighted errors before updating.');
    }
    return !hasError;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        username: formData.username,
        email: formData.email,
        fullname: formData.fullname,
        role: formData.role,
      };

      if (formData.password) {
        payload.password = formData.password;
        payload.confirm_password = formData.confirmPassword;
      }

      const response = await axios.put(`${API_BASE_URL}/update_user/${user_id}`, payload, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      });

      if (response.data.status === 'success') {
        showNotification('success', `User info updated successfully for ${formData.fullname}!`);
        setTimeout(() => navigate('/home', { state: { activeMenu: 'System User' } }), 1000);
      } else {
        showNotification('error', response.data.message || 'An error occurred.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Server connection error.';
      showNotification('error', errorMessage);

      if (errorMessage.toLowerCase().includes('already exists')) {
        setErrors(prev => ({
          ...prev,
          username: errorMessage.includes('username') ? 'Username may already be taken' : prev.username,
          email: errorMessage.includes('email') ? 'Email may already be registered' : prev.email,
        }));
      }
    } finally {
      setLoading(false);
    }
  };


  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 4 }}>
          <button
            onClick={() => navigate('/home', { state: { activeMenu: 'System User' } })}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#008080',
              fontSize: '16px',
              fontWeight: 500,
              cursor: 'pointer',
              marginRight: '16px',
            }}
          >
            ← Back
          </button>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
            {/* Heading with Icon */}
            <Typography
              variant="h4"
              sx={{
                color: '#008080',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >

              Edit User
            </Typography>

            {/* Right-aligned Logo and Text */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography
                variant="h6"
                sx={{
                  color: '#008080',
                  marginLeft: 1,
                  fontWeight: 500,
                  letterSpacing: 0.3,
                }}
              >
                OTMS
              </Typography>
            </Box>
          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleNameChange}
                  required
                  fullWidth
                  error={!!errors.username}
                  helperText={errors.username}
                  onBlur={handleBlur}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Full Name"
                  name="fullname"
                  value={formData.fullname}
                  onChange={handleNameChange}
                  required
                  fullWidth
                  error={!!errors.fullname}
                  helperText={errors.fullname}
                  onBlur={handleBlur}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email}
                  onBlur={handleBlur}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  select
                  fullWidth
                  error={!!errors.role}
                  helperText={errors.role}
                  onBlur={handleBlur}
                >
                  {roles.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  fullWidth
                  error={!!errors.password}
                  helperText={errors.password}
                  onBlur={handleBlur}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {!errors.password && formData.password && (
                  <FormHelperText>
                    Password must be at least 8 characters with uppercase, lowercase and numbers
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  fullWidth
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  onBlur={handleBlur}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          aria-label="toggle confirm password visibility"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>



              <Grid item xs={12}>
                {/* Save Draft Button */}
                {/* <DraftSaveButton
                formKey={DRAFT_KEYS.EDIT_USER}
                uhidNo={user_id || 'default'}
                formData={formData}
                onDraftLoaded={handleDraftLoaded}
              /> */}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={loading}
                >
                  Update User
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default EditUser;

